import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  ExternalLink,
  MapPin,
  // Share2,
  // Megaphone,
  // ChevronRight,
  Instagram as InstagramIcon,
  Linkedin as LinkedinIcon,
  Facebook as FacebookIcon,
  BookOpen,
} from "lucide-react";
import job_fair from "../assets/job_fair.png";
import employee from "../assets/EmployeeExperience_Header_1000x750-768x576.jpg";

import { fetchLatestNews } from "../api/controller/admin/adminLatestNewsController";
import { fetchNewsletters } from "../api/controller/admin/adminNewsLetters";
import { fetchUpcomingEvents } from "../api/controller/admin/adminUpcommingEventsController";
import { fetchSocialMedia } from "../api/controller/admin/adminSocialMedia";
import { fetchSocialMediaUpdates } from "../api/controller/admin/adminSocialMediaUpdatesController";
import { formatDisplayDate } from "../pages/helper/date";

const resolveSocialIcon = (platformName = "") => {
  const normalized = platformName.toLowerCase();
  if (normalized.includes("instagram")) return InstagramIcon;
  if (normalized.includes("linkedin")) return LinkedinIcon;
  if (normalized.includes("facebook")) return FacebookIcon;
  return null;
};
const NewsPage = () => {
  const [expandedArticles, setExpandedArticles] = useState({});
  const [newsItems, setNewsItems] = useState([]);
  const [newsletterItems, setNewsletterItems] = useState([]);
  const [socialMedia, setSocialMedia] = useState([]);
  const [socialUpdates, setSocialUpdates] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const toggleArticle = (id) => {
    setExpandedArticles((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          newsResult,
          newslettersResult,
          eventsResult,
          socialResult,
          updatesResult,
        ] = await Promise.allSettled([
          fetchLatestNews({ active: true }),
          fetchNewsletters({ active: true }),
          fetchUpcomingEvents({ active: true }),
          fetchSocialMedia({ active: true }),
          fetchSocialMediaUpdates({ active: true }),
        ]);

        if (newsResult.status === "fulfilled") {
          const data = newsResult.value;
          setNewsItems(Array.isArray(data) ? data : []);
        } else {
          console.error("Failed to load latest news.", newsResult.reason);
          setNewsItems([]);
        }

        if (newslettersResult.status === "fulfilled") {
          const ndata = newslettersResult.value;
          setNewsletterItems(Array.isArray(ndata) ? ndata : []);
        } else {
          console.error(
            "Failed to load newsletters.",
            newslettersResult.reason
          );
          setNewsletterItems([]);
        }

        if (eventsResult.status === "fulfilled") {
          const ev = eventsResult.value;
          setUpcomingEvents(Array.isArray(ev) ? ev : []);
        } else {
          console.error("Failed to load upcoming events.", eventsResult.reason);
          setUpcomingEvents([]);
        }

        if (socialResult.status === "fulfilled") {
          const sm = socialResult.value;
          setSocialMedia(Array.isArray(sm) ? sm : []);
        } else {
          console.error("Failed to load social media.", socialResult.reason);
          setSocialMedia([]);
        }

        if (updatesResult.status === "fulfilled") {
          const su = updatesResult.value;
          setSocialUpdates(Array.isArray(su) ? su : []);
        } else {
          console.error(
            "Failed to load social media updates.",
            updatesResult.reason
          );
          setSocialUpdates([]);
        }
      } catch (err) {
        console.error("Unexpected error loading News page data.", err);
        setNewsItems([]);
        setNewsletterItems([]);
        setUpcomingEvents([]);
        setSocialMedia([]);
        setSocialUpdates([]);
      }
    };

    fetchData();
  }, []);

  const navigate = useNavigate();

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const buildEmbedConfig = (platformName = "", embedUrl = "", key) => {
    const url = String(embedUrl || "").trim();
    if (!url) return null;

    const platform = platformName.toLowerCase();

    if (platform.includes("facebook")) {
      const src = url.includes("facebook.com/plugins/post.php")
        ? url
        : `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(
            url
          )}&show_text=true&width=500`;
      return {
        title: `facebook-post-${key}`,
        src,
        width: "100%",
        height: "600",
        allow:
          "autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share",
        allowFullScreen: true,
        style: {
          border: "none",
          overflow: "hidden",
          maxWidth: "500px",
          width: "100%",
        },
      };
    }

    if (platform.includes("instagram")) {
      const trimmed = url.replace(/\/?$/, "");
      const src = trimmed.includes("/embed") ? trimmed : `${trimmed}/embed`;
      return {
        title: `instagram-post-${key}`,
        src,
        width: "100%",
        height: "650",
        style: {
          border: "none",
          overflow: "hidden",
          maxWidth: "500px",
          width: "100%",
        },
        allowFullScreen: true,
      };
    }

    if (platform.includes("linkedin")) {
      const urnMatch = url.match(/urn:li:[^/?&#]+/);
      const src = url.includes("linkedin.com/embed/feed/update")
        ? url
        : urnMatch
        ? `https://www.linkedin.com/embed/feed/update/${urnMatch[0]}`
        : url;
      return {
        title: `linkedin-post-${key}`,
        src,
        width: "100%",
        height: "600",
        style: {
          border: "none",
          overflow: "hidden",
          maxWidth: "500px",
          width: "100%",
        },
        allowFullScreen: true,
      };
    }

    return {
      title: `social-post-${key}`,
      src: url,
      width: "100%",
      height: "500",
      style: {
        border: "none",
        overflow: "hidden",
        maxWidth: "500px",
        width: "100%",
      },
      allowFullScreen: true,
    };
  };

  const renderSocialEmbed = (update, platformName, index) => {
    const config = buildEmbedConfig(
      platformName,
      update?.embedUrl,
      update?._id || update?.id || index
    );
    if (!config) return null;

    const { style, allowFullScreen, ...rest } = config;

    return (
      <div className="mt-4 flex justify-center">
        <iframe
          {...rest}
          style={style}
          frameBorder="0"
          allowFullScreen={Boolean(allowFullScreen)}
        ></iframe>
      </div>
    );
  };

  const socialIconLinks = useMemo(() => {
    const seen = new Set();
    return socialMedia.filter((item) => {
      const url = (item?.url || item?.link || "").trim();
      if (!url) return false;
      const key = url.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [socialMedia]);

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ fontFamily: "'corporate-s', serif" }}
    >
      {/* Header */}
      <header
        className="text-center py-16 bg-[#00244D] relative overflow-hiddenh-[300px] sm:h-[380px] md:h-[450px] lg:h-[500px]
 flex items-center "
        style={{
          backgroundImage: `url(${employee})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#003DA5] opacity-60"></div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="text-4xl text-[#fff] mb-6 sm:text-6xl font-bold tracking-wide font-['corporate-s']">
            News and Updates
          </h1>

          {/*  <div className="rounded-lg p-6 max-w-2xl mx-auto mt-6 bg-[#f0f0f0]">
            <p className="text-sm sm:text-base font-regular font-['corporate-s']">
              TWA delivers impactful updates, inspiring stories, and opportunities
              to engage with our mission of empowering justice-involved
              jobseekers.
            </p>
          </div> */}
        </div>
      </header>

      <section className="relative bg-gray-200 py-16">
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="bg-white relative p-6 md:px-6 md:w-[300px] shadow-xl mx-auto max-w-sm md:max-w-xl">
            <h2 className="text-md sm:text-xl md:text-2xl font-bold font-['corporate']  mb-4 text-[#003DA5] text-center">
              Stay connected with the latest developments, success stories, and
              upcoming events from Saint Louis University's Transformative
              Workforce Academy.
            </h2>
            <p className="text-sm sm:text-md justify-center font-['corporate-s'] text-left">
              TWA delivers impactful updates, inspiring stories, and
              opportunities to engage with our mission of empowering
              justice-involved jobseekers.
            </p>
          </div>
        </div>
      </section>

      {/* COLOR BLOCK SECTION */}
      {/*  <section className="relative bg-gray-200 py-16 h-[50px] md:h-[150px]">
        {/* OVERLAPPING CONTENT BOX *
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center px-6">
          <div className="bg-white relative p-6 md:px-6 w-md shadow-xl mx-auto max-w-sm h-lg md:max-w-xl">
            <h2 className="text-xl sm:text-lg md:text-2xl font-bold font-['corporate']  mb-4 text-[#003DA5] text-center">
        Stay connected with the latest developments, success stories,  <br />and upcoming events from 
        <br />Saint Louis University's <br />Transformative Workforce Academy.
            </h2>
            <p className="text-md justify-center font-['corporate-s'] text-left">
              TWA delivers impactful updates, inspiring stories, 
              and opportunities to engage with our mission of empowering justice-involved jobseekers.
            </p>

          </div>
        </div>
      </section> */}

      {/* Navigation Cards - HORIZONTAL SCROLLABLE */}
      {/* <section className="pb-16 pt-40 px-4 sm:px-6 bg-[#fff]">
        <div className="max-w-6xl mx-auto">
             <div className="flex flex-wrap gap-8 justify-center">
         {/* <div className="flex overflow-x-auto space-x-8 pb-4 snap-x snap-mandatory scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"> */}
      {/*      {[
              { key: "news", label: "Latest News", icon: Megaphone, color: "#003DA5" },
              { key: "newsletters", label: "Newsletters and Blogs", icon: BookOpen, color: "#53C3EE" },
              { key: "events", label: "Upcoming Events", icon: Calendar, color: "#00244D" },
              { key: "social", label: "Social Media", icon: Share2, color: "#FFC72C" },
            ].map(({ key, label, icon: Icon, color }) => (
              <a
                key={key}
                href={`#${key}`}
                //className="bg-white rounded-lg p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-l-4 text-left group flex-shrink-0 w-full max-w-sm snap-start"
                         className="bg-white p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-l-4 text-left group w-full sm:w-[300px] md:w-[250px]" 
                style={{ borderColor: color }}
              >
              {/*}  <Icon className="w-12 h-12 mb-4" style={{ color }} /> */}
      {/*}       <h3 className="text-2xl font-bold mb-3 font-['corporate-s']" style={{ color }}>
                  {label.toUpperCase()}
                </h3>
           {/*     <p className="text-gray-600 font-['corporate-s']">
                  {key === "news"
                    ? "Discover TWA's latest achievements and announcements."
                    : key === "newsletters"
                    ? "Read our insights, reports, and success stories."
                    : key === "events"
                    ? "Join us for job fairs, workshops, and more."
                    : "Connect with our community on social platforms."}
                </p> */}
      {/* <ChevronRight className="w-6 h-6 mt-2 transition-transform group-hover:translate-x-2" style={{ color }} /> */}
      {/*         </a>               
            ))}
          </div>
        </div>
      </section> */}

      <section className="pt-50 md:pb-35 pb-50  bg-white">
        <div className="max-w-6xl h-55 mx-auto md:px-0 px-6  grid grid-cols-1 sm:grid-cols-4 gap-6">
          {[
            { title: "Latest News", color: "#003DA5", id: "news" },
            {
              title: "Newsletters and Blogs",
              color: "#FFC72C",
              id: "newsletters",
            },
            { title: "Upcoming Events", color: "#668ac9", id: "events" },
            { title: "Social Media", color: "#003DA5", id: "social" },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white p-6 flex flex-col justify-center items-center shadow-lg/30 hover:shadow-2xl transition-all duration-300 text-center sm:text-left hover:-translate-y-2 border-l-4 cursor-pointer"
              onClick={() => scrollToSection(item.id)}
              // onClick={() => (window.location.href = `#${item.id}`)}
              style={{ borderColor: item.color }}
            >
              <h3
                className="text-3xl sm:text-xl md:text-4xl lg:text-5xl font-bold font-['corporate-s']"
                style={{ color: item.color }}
              >
                {item.title}
              </h3>
            </div>
          ))}
        </div>
      </section>

      {/* News Section */}
      <section id="news" className="py-12 md:px-0 px-4 bg-white">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 pb-20">
            <div className="md:col-start-1 col-span-2 md:ml-20 flex justify-center md:justify-start">
              <h2 className="text-4xl sm:text-5xl font-bold text-[#003DA5] font-['corporate-s'] text-center md:text-left tracking-tight">
                Latest News
              </h2>
            </div>

            {/* === Section PARAGRAPH === */}
            <div className="md:col-start-3 md:col-span-3 flex justify-center items-center">
              <p className="font-['farnham-display'] text-xl md:text-3xl leading-tight text-left md:text-left">
                Stay informed about TWA's impact, partnerships, and program
                developments.
              </p>
            </div>
          </div>
        </div>

        {/* Featured Article */}
        {newsItems.map((article) => (
          <div
            key={article.id}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 shadow-md mb-12"
          >
            <div className="md:col-span-1">
              <div className="w-full h-[250px] sm:h-[300px] overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
            <div className="md:col-span-2 px-6">
              <div className="flex items-center pt-6 mb-3">
                {/* <span className="bg-blue-100 text-blue-800 px-3 py-1  text-sm font-semibold mr-3 font-['corporate-s']">
                    FEATURED
                  </span> */}
                <span className="text-sm text-gray-600 font-['corporate-s']">
                  {article.category}
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-3 text-[#00244D] font-['corporate-s']">
                {article.title}
              </h3>
              <div
                className={`transition-all duration-500 ease-in-out ${
                  expandedArticles[article.id]
                    ? "max-h-[500px] opacity-100"
                    : "max-h-20 opacity-75"
                } overflow-hidden`}
              >
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 font-['corporate-s']">
                  {expandedArticles[article.id]
                    ? article.excerpt
                    : `${article.excerpt.slice(0, 100)}...`}
                </p>
              </div>
              <div className="flex items-center justify-between text-sm text-[#2b2b2b] font-['corporate-s']">
                <div className="flex items-center gap-4">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{article.readTime}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>{formatDisplayDate(article.date)}</span>
                  </div>
                </div>
                <button
                  onClick={() => toggleArticle(article.id)}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {expandedArticles[article.id] ? "READ LESS" : "READ MORE"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Newsletters Section */}
      <section id="newsletters" className="py-12 md:px-0 px-4 bg-white">
        <div className="mx-auto max-w-7xl md:pr-20">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-y-8 md:gap-x-6">
            {/* === Letters and blogs HEADING === */}
            <div className="md:col-start-1 flex justify-center md:justify-start">
              <h2 className="text-4xl sm:text-5xl font-bold text-[#FFC72C] md:ml-20 font-['corporate-s'] text-center md:text-left tracking-tight">
                Newsletters and Blogs
              </h2>
            </div>

            {/* === section PARAGRAPH === */}
            <div className="md:col-start-3 md:col-span-3 flex justify-center items-center">
              <p className="font-['farnham-display'] text-xl md:text-3xl leading-tight text-left md:text-left">
                Access our regular publications featuring insights, research,
                and impact stories.
              </p>
            </div>

            {/* === MISSION CONTENT BOX === */}
            <div className="md:col-start-3 md:col-span-3">
              <div className="grid gap-8">
                {newsletterItems.map((newsletter) => (
                  <div
                    key={newsletter.id}
                    className="flex items-start py-10 bg-[#f0f0f0]"
                  >
                    <div className="border-l-6  border-[#FFC72C] ml-6 pl-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-3">
                            <BookOpen className="w-5 h-5 text-[#53C3EE] mr-2" />
                            <span className="bg-gray-100 text-gray-800 px-3 py-1  text-sm font-medium font-['corporate-s']">
                              {newsletter.type}
                            </span>
                            <span className="text-sm text-gray-600 ml-4 font-['corporate-s']">
                              {formatDisplayDate(newsletter.date)}
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold mb-3 text-[#00244D] font-['corporate-s']">
                            {newsletter.title}
                          </h3>
                          <p className="text-sm text-gray-700 leading-relaxed mb-4 font-['corporate-s']">
                            {newsletter.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <a
                          href={newsletter.downloadLink}
                          className="flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors font-['corporate-s']"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          READ FULL ARTICLE
                        </a>
                        {/* <button className="flex items-center text-gray-700 hover:text-blue-600 transition-colors font-['corporate-s']">
                    <Mail className="w-4 h-4 mr-2" />
                    SUBSCRIBE
                  </button> */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-y-8 md:gap-x-6">
            {/* === section CONTENT BOX === */}
            <div className="md:col-start-3 md:col-span-3"></div>
          </div>
        </div>

        {/* <div className="max-w-full mx-auto">
          <div className="flex flex-col md:flex-row items-center md:items-start md:justify-start justify-center gap-6 mb-10 text-center md:text-left">
           {/* <BookOpen className="w-8 h-8 mr-3" style={{ color: "#53C3EE" }} /> *
            <h2 className="text-3xl sm:text-4xl md:pl-20 md:text-5xl font-['corporate-s'] font-bold text-[#53C3EE] md:w-1/3" style={{ color: "#53C3EE" }}>
              Newsletters and Blogs
            </h2>
                      <p className="text-sm sm:text-base md:text-2xl leading-relaxed text-[#2b2b2b] font-['farnham-display'] max-w-2xl md:w-2/3">
            Access our regular publications featuring insights, research, and impact stories.
          </p>
          </div>


          <div className="grid gap-8">
            {newsletterItems.map((newsletter) => (

                  <div  
                  key={newsletter.id} className="flex items-start">
      
      <div className="hidden md:block md:w-1/3">
      </div>
              <div
                className="bg-white p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-l-4 border-[#53C3EE]  md:w-2/3"
                
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <BookOpen className="w-5 h-5 text-[#53C3EE] mr-2" />
                      <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium font-['corporate-s']">
                        {newsletter.type}
                      </span>
                      <span className="text-sm text-gray-600 ml-4 font-['corporate-s']">
                        {formatDisplayDate(newsletter.date)}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-[#00244D] font-['corporate-s']">
                      {newsletter.title}
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4 font-['corporate-s']">
                      {newsletter.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <a
                    href={newsletter.downloadLink}
                    className="flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors font-['corporate-s']"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    READ FULL ARTICLE
                  </a>
                  {/* <button className="flex items-center text-gray-700 hover:text-blue-600 transition-colors font-['corporate-s']">
                    <Mail className="w-4 h-4 mr-2" />
                    SUBSCRIBE
                  </button> *
                </div>
              </div>
              </div>
            ))}
          </div>

          {/* Newsletter Signup */}
        {/* <div className="bg-gray-50 rounded-lg p-6 mt-12 text-center">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4 font-['HVD_Trial_Brandon_Grotesque'] text-[#00244D]">
              STAY INFORMED
            </h3>
            <p className="text-sm sm:text-base mb-6 font-['corporate-s'] text-gray-700">
              Subscribe to our newsletter for the latest updates, success
              stories, and opportunities to get involved with TWA.
            </p>
            <div className="flex flex-col md:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
              />
              <button className="bg-[#003DA5] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#002d7d] transition-colors font-['corporate-s']">
                SUBSCRIBE
              </button>
            </div>
          </div> *
        </div> */}
      </section>

      {/* Events Section */}
      <section id="events" className="py-12 md:px-0 px-4 bg-[#fff]">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 pb-20">
            <div className="md:col-start-1 col-span-2 md:ml-20 flex justify-center md:justify-start">
              <h2 className="text-4xl sm:text-5xl font-bold text-[#668ac9] font-['corporate-s'] text-left md:text-left tracking-tight">
                Upcoming Events
              </h2>
            </div>

            {/* === Section PARAGRAPH === */}
            <div className="md:col-start-3 md:col-span-3 flex justify-center items-center">
              <p className="font-['farnham-display'] text-xl md:text-3xl leading-tight text-left md:text-left">
                Join us at upcoming job fairs, workshops, and community events.
              </p>
            </div>
          </div>
        </div>
        <div className="max-w-full mx-auto">
          {/* Featured Events */}
          <div className="grid gap-8 mb-12">
            {upcomingEvents
              .filter((event) => event.featured)
              .map((event) => (
                <div
                  key={event.id}
                  className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 shadow-md"
                >
                  <div className="md:col-span-1">
                    <div className="w-full h-full sm:h-full overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                      <img
                        src={event.image || job_fair}
                        alt={event.title}
                        className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2 border-l-4 border-[#003DA5] border-l-6 my-10  border-[#FFC72C] ml-6 pl-4 md:pr-20">
                    <div className="flex items-center mb-3">
                      <span className="bg-blue-100 text-blue-800 px-3 py-1  text-sm font-semibold mr-3 font-['corporate-s']">
                        FEATURED EVENT
                      </span>
                      <span className="bg-gray-100 text-gray-800 px-3 py-1  text-sm font-['corporate-s']">
                        {event.category}
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold mb-3 font-['corporate-s'] text-[#00244D]">
                      {event.title}
                    </h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 mr-3 text-gray-600" />
                        <span className="font-['corporate-s'] text-sm">
                          {event.startDateTime
                            ? new Date(event.startDateTime).toLocaleDateString()
                            : event.date}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 mr-3 text-gray-600" />
                        <span className="font-['corporate-s'] text-sm">
                          {event.startDateTime
                            ? new Date(event.startDateTime).toLocaleTimeString(
                                [],
                                { hour: "2-digit", minute: "2-digit" }
                              )
                            : event.startTime}
                          {event.endDateTime
                            ? ` - ${new Date(
                                event.endDateTime
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`
                            : event.endTime
                            ? ` - ${event.endTime}`
                            : ""}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-5 h-5 mr-3 text-gray-600" />
                        <span className="font-['corporate-s'] text-sm">
                          {event.location}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base mb-4 leading-relaxed font-['corporate-s'] text-gray-700">
                      {event.description}
                    </p>
                    <a
                      href={event.registrationLink}
                      className="bg-[#003DA5] text-white px-6 py-3  font-semibold hover:bg-[#002d7d] transition-colors font-['corporate-s']"
                    >
                      REGISTER NOW
                    </a>
                  </div>
                </div>
              ))}
          </div>

          {/* Regular Events */}
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-start-3 md:col-span-4 md:pr-20">
                <div className="grid gap-8">
                  {upcomingEvents
                    .filter((event) => !event.featured)
                    .map((event) => (
                      <div
                        key={event.id}
                        className="flex items-start py-10 bg-[#f0f0f0]"
                      >
                        <div className="border-l-6  border-[#FFC72C] ml-6 pl-4">
                          {/* <div className="flex items-start  md:items-center justify-between"> */}
                          <div className="flex items-center mb-3">
                            <span className="bg-gray-100 text-gray-800 px-3 py-1  text-sm font-medium font-['corporate-s']">
                              {event.category}
                            </span>
                            <div className="flex items-center text-sm text-gray-600 ml-4 font-['corporate-s']">
                              <Calendar className="w-4 h-4 mr-1" />
                              <span className="mr-4">
                                {event.startDateTime
                                  ? new Date(
                                      event.startDateTime
                                    ).toLocaleDateString()
                                  : event.date}
                              </span>
                              <Clock className="w-4 h-4 mr-1" />
                              <span>
                                {event.startDateTime
                                  ? new Date(
                                      event.startDateTime
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : event.startTime}
                                {event.endDateTime
                                  ? ` - ${new Date(
                                      event.endDateTime
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}`
                                  : event.endTime
                                  ? ` - ${event.endTime}`
                                  : ""}
                              </span>
                            </div>
                          </div>
                          <h3 className="text-2xl font-bold mb-2 text-[#00244D] font-['corporate-s']">
                            {event.title}
                          </h3>
                          <div className="flex items-center mb-3">
                            <MapPin className="w-4 h-4 text-gray-600 mr-2" />
                            <span className="text-gray-600 font-['corporate-s'] text-sm">
                              {event.location}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed font-['corporate-s']">
                            {event.description}
                          </p>

                          <div className="mt-4 md:mt-6 flex justify-start items-left">
                            <a
                              href={event.registrationLink}
                              className="w-auto md:w-auto bg-[#003DA5] text-white px-6 py-3 font-semibold hover:bg-[#002d7d] transition-colors font-['corporate-s']"
                            >
                              REGISTER
                            </a>
                          </div>
                        </div>
                      </div>
                      // </div>
                    ))}
                </div>
              </div>
              <div className="md:col-start-3 md:col-span-4 md:pr-20">
                {/* Call to Action */}
                <div className="bg-[#f0f0f0]  py-10 mt-12">
                  <div className="flex">
                    <div className="border-l-6  border-[#FFC72C] ml-6 pl-4"></div>

                    <div className="text-left">
                      <h3 className="text-2xl sm:text-3xl font-bold mb-4 font-['corporate-s'] text-[#00244D]">
                        DON'T SEE WHAT YOU'RE LOOKING FOR?
                      </h3>
                      <p className="text-sm sm:text-base mb-6 font-['corporate-s'] text-gray-700">
                        Contact us to learn about additional events, workshops,
                        or ways to get involved with TWA.
                      </p>
                      <button
                        onClick={() => {
                          navigate("/contact");
                        }}
                        className="bg-[#003DA5] text-white px-6 py-3  font-semibold hover:bg-[#002d7d] transition-colors font-['corporate-s']"
                      >
                        CONTACT US
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section id="social" className="py-12 md:px-0 px-4 bg-[#fff]">
        <div className="max-w-7xl mx-auto md:pr-20">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 pb-20">
            <div className="md:col-start-1 col-span-2 md:ml-20 flex justify-center md:justify-start">
              <h2 className="text-4xl sm:text-5xl font-bold text-[#003DA5] font-['corporate-s'] text-left md:text-left tracking-tight">
                Follow Us on Social Media
              </h2>
            </div>
            {/* === section PARAGRAPH === */}
            <div className="md:col-start-3 md:col-span-4 flex justify-center items-center">
              <p className="font-['farnham-display'] text-xl md:text-3xl leading-tight text-left md:text-left">
                Stay connected and engaged with our community across all
                platforms.
              </p>
            </div>
          </div>

          {/* Social Media Cards */}
          <div className="flex">
            <div className="w-1/3 hidden md:block"></div>
            <div className="md:w-2/3 xs:w-full sm:w-full sm:justify-center">
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {socialMedia.map((platform, index) => {
                  const Icon = platform.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white  p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-l-4 border-[#FFC72C]"
                    >
                      <div
                        className={`${platform.color} p-4  text-white text-center mb-4`}
                      >
                        {/* icon can be a React component (from static data) or a URL string from the API */}
                        {typeof platform.icon === "string" ? (
                          <img
                            src={platform.icon}
                            alt={`${platform.platform} icon`}
                            className="w-10 h-10 mx-auto mb-3 object-contain"
                          />
                        ) : (
                          Icon && <Icon className="w-10 h-10 mx-auto mb-3" />
                        )}
                        <h3 className="text-lg font-bold mb-2 font-['corporate-s']">
                          {platform.platform.toUpperCase()}
                        </h3>
                        <p className="text-sm font-['corporate-s']">
                          {platform.followers}+ FOLLOWERS
                        </p>
                      </div>
                      <p className="text-sm text-gray-700 mb-4 leading-relaxed font-['corporate-s']">
                        {platform.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 font-['corporate-s']">
                          {platform.handle}
                        </span>
                        <a
                          href={platform.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors font-['corporate-s']"
                        >
                          FOLLOW <ExternalLink className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Social Feed Preview */}
          <div className="flex">
            <div className="w-1/3 hidden md:block"></div>
            <div className="md:w-2/3 xs:w-full sm:w-full sm:justify-center">
              <div className="bg-white  p-6 shadow-md">
                <h3 className="text-2xl sm:text-3xl font-bold mb-6 text-left font-['corporate-s'] text-[#00244D]">
                  RECENT SOCIAL MEDIA UPDATES
                </h3>
                <div className="space-y-6">
                  {(showAllUpdates
                    ? socialUpdates
                    : socialUpdates.slice(0, 3)
                  ).map((update, i) => {
                    const platformName =
                      update.socialMediaPlatform ||
                      update.platform ||
                      (update.socialMedia &&
                        (update.socialMedia.platform ||
                          update.socialMedia.name)) ||
                      "Social";
                    // Determine icon: prefer API-provided icon URL, then icon component, then a mapping by platform name
                    const iconUrl =
                      typeof update.socialMediaIcon === "string"
                        ? update.socialMediaIcon
                        : typeof update.icon === "string"
                        ? update.icon
                        : null;
                    const IconComp =
                      typeof update.icon === "function"
                        ? update.icon
                        : platformName.toLowerCase().includes("instagram")
                        ? InstagramIcon
                        : platformName.toLowerCase().includes("linkedin")
                        ? LinkedinIcon
                        : platformName.toLowerCase().includes("facebook")
                        ? FacebookIcon
                        : null;
                    const embed = renderSocialEmbed(update, platformName, i);

                    return (
                      <div className="bg-white-50  p-6" key={i}>
                        <div className="flex items-center mb-2">
                          {iconUrl ? (
                            <img
                              src={iconUrl}
                              alt={`${platformName} icon`}
                              className="w-8 h-8 mr-2 object-contain"
                            />
                          ) : (
                            IconComp && (
                              <IconComp className="w-5 h-5 text-blue-600 mr-2" />
                            )
                          )}
                          <span className="font-semibold text-[#00244D] font-['corporate-s']">
                            {platformName.toUpperCase()}
                          </span>
                          <span className="text-gray-600 text-sm ml-2 font-['corporate-s']">
                            •{" "}
                            {update.createdAt
                              ? formatDisplayDate(update.createdAt)
                              : "Just now"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed font-['corporate-s']">
                          {update.description}
                        </p>
                        {embed}
                      </div>
                    );
                  })}
                </div>

                <div className="text-right mt-6">
                  <button
                    onClick={() => setShowAllUpdates((v) => !v)}
                    className="bg-[#003DA5] text-white px-6 py-3  font-semibold hover:bg-[#002d7d] transition-colors font-['corporate-s']"
                    aria-expanded={showAllUpdates}
                  >
                    {showAllUpdates
                      ? "VIEW LESS SOCIAL MEDIA UPDATES"
                      : "VIEW ALL SOCIAL MEDIA UPDATES"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 px-4 sm:px-6 bg-[#00244D] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-regular mb-6 font-['corporate-s']">
            Stay Connected with TWA
          </h2>
          <p className="text-sm sm:text-base mb-6 leading-relaxed font-['corporate-s']">
            Join our community of supporters, employers, and advocates working
            to create second chances.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* <button className="bg-[#003DA5] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#002d7d] transition-colors font-['corporate-s']">
              SUBSCRIBE TO NEWSLETTER
            </button> */}
            <button className="bg-white/10 border-2 border-white text-white px-6 py-3  font-semibold hover:bg-white/20 transition-colors font-['corporate-s']">
              FOLLOW ON SOCIAL MEDIA
            </button>
          </div>

          {socialIconLinks.length > 0 && (
            <div className=" mt-6 flex justify-center gap-5">
              {socialIconLinks.map((item) => {
                const url = (item?.url || item?.link || "").trim();
                const key = item?.id || url;
                if (!url || !key) return null;

                const iconSource = item?.icon;
                const iconLabel =
                  item?.platform || item?.name || "social media";

                if (typeof iconSource === "string" && iconSource.trim()) {
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="gb-white text-white hover:text-[#FFC72C] transition-colors"
                      aria-label={`Visit ${iconLabel}`}
                    >
                      <img
                        src={iconSource}
                        alt={`${iconLabel} icon`}
                        className="w-16 h-16 object-contain"
                      />
                    </a>
                  );
                }

                const IconComponent =
                  typeof iconSource === "function"
                    ? iconSource
                    : resolveSocialIcon(iconLabel);

                const IconToRender = IconComponent || ExternalLink;

                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-[#FFC72C] transition-colors"
                    aria-label={`Visit ${iconLabel}`}
                  >
                    <IconToRender className="w-7 h-7" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
